import { useState } from 'react';
import { Download, FileSignature, FileText } from 'lucide-react';
import {
  type SurveyDto,
} from '@eombor/contracts';
import { Badge, Button, Card, Table, cn, type Column } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { RejectDialog } from '../../requisition/ui/RejectDialog.js';
import { diffText, formatDate, qtyDiff, qtyText, surveyStatusTone } from '../model/format.js';
import { surveyStatusText } from '../../../shared/i18n/labels.js';

/**
 * Kartochkadagi satr — ARIZA satri va hujjat satri BIRLASHTIRILGAN ko'rinishi.
 *
 * Buxgalter oldida bitta savol turadi: "so'ralgan bilan tasdiqlangan bir
 * xilmi?". Ikki jadvalga ajratilsa, u savolga javob berish uchun ko'zni
 * ekranning ikki chetiga yugurtirish kerak bo'lardi.
 */
export interface SurveyCardLine {
  id: string;
  lineNo: number;
  name: string;
  /** Nomenklatura kodi — ma'lumotnoma yuklanmagan bo'lsa `null`. */
  code: string | null;
  /** O'lchov birligi yorlig'i ("dona", "kg") — noma'lum bo'lsa `null`. */
  unit: string | null;
  requestedQty: number;
  approvedQty: number | null;
}

export interface SurveyCardProps {
  survey: SurveyDto;
  lines: SurveyCardLine[];
  /** Talabnoma maqsadi — hujjatda saqlanmaydi, talabnomadan olinadi. */
  purpose?: string | null;
  warehouseName?: string | null;
  orgUnitName?: string | null;
  /**
   * Tugmalar KO'RINISHI. Ruxsati yo'q xodimga tugma umuman chizilmaydi —
   * o'chirilgan holda ham emas. Haqiqiy qarorni baribir backend chiqaradi.
   */
  canApprove?: boolean;
  canReject?: boolean;
  pending?: boolean;
  /** Serverdan kelgan xato — odam uchun yozilgan matn. */
  error?: string | null;
  onApprove: () => void;
  /** Sabab BO'SH JOYLARSIZ keladi — server ham aynan shu matnni ko'radi. */
  onReject: (reason: string) => void;
  onDownloadPdf?: () => void;
  pdfPending?: boolean;
  /**
   * TREBOVANIYA blankini ochadi (ФМУ №25).
   *
   * `onDownloadPdf` dan AYRI: u ichki qisqacha, bu esa buxgalterga
   * IMZO bilan topshiriladigan rasmiy qog'oz. Ikkisini bitta tugmaga
   * yig'ish "qaysi biri bosiladi?" degan savolni tug'dirardi.
   */
  onOpenRequirement?: () => void;
}

interface FactProps {
  label: string;
  children: React.ReactNode;
}

const Fact = ({ label, children }: FactProps) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <dt className="text-xs text-text-faint">{label}</dt>
    <dd className="min-w-0 text-sm text-text">{children}</dd>
  </div>
);

/**
 * So'rovnoma KARTOCHKASI — buxgalterning qaror ekrani.
 *
 * Komponent so'rovni O'ZI yubormaydi: tasdiqlash ham, rad etish ham aslida
 * ARIZA o'tishi (`ACCOUNTING_APPROVED` / `REJECTED`), shuning uchun amal
 * chaqiruv orqali tashqariga beriladi. Shu sabab kartochkani ro'yxatda ham,
 * talabnoma ekranida ham bir xil ishlatish mumkin va u o'tish qoidalarini
 * bilmaydi — ular state machine da, bitta joyda turadi.
 */
export const SurveyCard = ({
  survey,
  lines,
  purpose = null,
  warehouseName = null,
  orgUnitName = null,
  canApprove = false,
  canReject = false,
  pending = false,
  error = null,
  onApprove,
  onReject,
  onDownloadPdf,
  pdfPending = false,
  onOpenRequirement,
}: SurveyCardProps) => {
  const [rejectOpen, setRejectOpen] = useState(false);

  const open = survey.status === 'PENDING';
  const hasDiff = lines.some((line) => qtyDiff(line.requestedQty, line.approvedQty) !== null);

  const columns: Column<SurveyCardLine>[] = [
    { key: 'lineNo', header: '№', mono: true, width: '3.5rem' },
    {
      key: 'name',
      header: t('accounting.colNomenclature'),
      render: (line) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-text">{line.name}</span>
          {line.code === null ? null : (
            <span className="tabular text-xs text-text-faint">{line.code}</span>
          )}
        </span>
      ),
    },
    {
      key: 'requestedQty',
      header: t('accounting.colRequested'),
      align: 'right',
      mono: true,
      width: '8rem',
      render: (line) =>
        `${qtyText(line.requestedQty)}${line.unit === null ? '' : ` ${line.unit}`}`,
    },
    {
      key: 'approvedQty',
      header: t('accounting.colApproved'),
      align: 'right',
      mono: true,
      width: '8rem',
      render: (line) =>
        line.approvedQty === null ? (
          <span className="text-text-faint">—</span>
        ) : (
          qtyText(line.approvedQty)
        ),
    },
    {
      key: 'diff',
      header: t('accounting.colDiff'),
      align: 'right',
      mono: true,
      width: '7rem',
      // Farq faqat HAQIQATDA bo'lganda ko'rinadi: nolni ham belgilash
      // jadvalni ogohlantirishlarga to'ldirib, muhimini ko'rsatmay qo'yardi.
      render: (line) => {
        const diff = qtyDiff(line.requestedQty, line.approvedQty);
        return diff === null ? (
          <span className="text-text-faint">—</span>
        ) : (
          <Badge tone="warn" mono>
            {diffText(diff)}
          </Badge>
        );
      },
    },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <header
        className={cn(
          'flex flex-col gap-4 rounded-[var(--radius-lg)] border border-border',
          'bg-surface p-4 shadow-[var(--shadow-1)]',
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="flex flex-wrap items-center gap-2">
              <FileSignature size={16} aria-hidden className="text-text-faint" />
              {/*
                Sarlavhada QOG'OZDAGI raqam turadi — buxgalter hujjatni
                aynan shu raqam bilan izlaydi va o'z daftarida ham u
                yozilgan. Tizimning ichki raqami ostida, xira: u
                havolalar uchun kerak, lekin odamning savoli emas.
              */}
              <h2 className="tabular text-xl font-semibold tracking-tight text-text">
                {survey.formNo ?? survey.docNo}
              </h2>
              <Badge tone={surveyStatusTone(survey.status)}>
                {surveyStatusText(survey.status)}
              </Badge>
              {hasDiff ? <Badge tone="warn">{t('accounting.diffBadge')}</Badge> : null}
            </span>
            {survey.formNo === null ? null : (
              <span className="tabular text-xs text-text-faint">
                {t('accounting.systemNo', { docNo: survey.docNo })}
              </span>
            )}
            {purpose === null ? null : (
              <p className="max-w-prose text-sm text-text-muted">{purpose}</p>
            )}
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {/* TREBOVANIYA — asosiy qog'oz, shuning uchun u ko'zga
                tashlanadigan tugmada turadi. */}
            {onOpenRequirement === undefined ? null : (
              <Button variant="secondary" size="sm" onClick={onOpenRequirement}>
                <FileText size={14} aria-hidden />
                {t('accounting.requirementAction')}
              </Button>
            )}
            {onDownloadPdf === undefined ? null : (
              <Button variant="ghost" size="sm" loading={pdfPending} onClick={onDownloadPdf}>
                <Download size={14} aria-hidden />
                {t('accounting.downloadPdf')}
              </Button>
            )}
          </div>
        </div>

        <dl className="grid gap-3 border-t border-border pt-3 sm:grid-cols-2 xl:grid-cols-4">
          <Fact label={t('accounting.fieldRequisition')}>
            <span className="tabular">{survey.requisitionDocNo}</span>
          </Fact>
          <Fact label={t('accounting.fieldWarehouse')}>
            {warehouseName ?? `#${survey.warehouseId}`}
          </Fact>
          <Fact label={t('accounting.fieldOrgUnit')}>
            {orgUnitName ?? (survey.orgUnitId === null ? '—' : `#${survey.orgUnitId}`)}
          </Fact>
          <Fact label={survey.approvedAt === null ? t('accounting.fieldCreatedAt') : t('accounting.fieldApprovedAt')}>
            <span className="tabular">
              {formatDate(survey.approvedAt ?? survey.createdAt) ?? '—'}
            </span>
          </Fact>
        </dl>

        {survey.rejectedReason === null ? null : (
          <p
            className={cn(
              'rounded-[var(--radius-sm)] border border-danger/25 bg-danger-soft',
              'px-3 py-2 text-sm text-danger',
            )}
          >
            <span className="font-semibold">{t('accounting.rejectedReasonLabel')}: </span>
            {survey.rejectedReason}
          </p>
        )}
      </header>

      <Card title={t('accounting.linesTitle')} padded={false}>
        <Table
          caption={t('accounting.linesCaption')}
          columns={columns}
          rows={lines}
          rowKey={(line) => line.id}
          className="rounded-none border-0"
        />
      </Card>

      {error === null ? null : (
        <p
          role="alert"
          className={cn(
            'rounded-[var(--radius-sm)] border border-danger/25 bg-danger-soft',
            'px-3 py-2 text-sm text-danger',
          )}
        >
          {error}
        </p>
      )}

      {open ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canReject ? (
            <Button variant="danger" disabled={pending} onClick={() => setRejectOpen(true)}>
              {t('accounting.reject')}
            </Button>
          ) : null}
          {canApprove ? (
            <Button loading={pending} onClick={onApprove}>
              {t('accounting.approve')}
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-text-faint">{t('accounting.decisionDone')}</p>
      )}

      {/* Rad etish oynasi ARIZA ekranidagisi bilan bitta: sabab uzunligi
          chegarasi ham, matni ham server uchun AYNAN bir xil bo'lishi kerak —
          rad etish ikkala ekranda ham bitta o'tishga (`REJECTED`) tushadi. */}
      <RejectDialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        pending={pending}
        error={error}
        onConfirm={(reason) => {
          setRejectOpen(false);
          onReject(reason);
        }}
      />
    </div>
  );
};
